import React, { useState } from "react";
import { api, errMsg, toPaise, fromPaise } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAsyncData } from "@/hooks/useAsyncData";
import { Money, MoneyInput, StatusBadge, EmptyState, LoadErrorState, StoreDatePicker } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, SplitSquareHorizontal } from "lucide-react";
import { toast } from "sonner";

export default function DiscrepanciesPage() {
  const { user, banks, storeId } = useApp();
  const [status, setStatus] = useState("all");
  const [allocTarget, setAllocTarget] = useState(null);
  const [allocRows, setAllocRows] = useState([]);
  const [allocNote, setAllocNote] = useState("");
  const [settleTarget, setSettleTarget] = useState(null);
  const [settle, setSettle] = useState({ amount: "", mode: "cash", bank_id: "", note: "", related_bill_no: "" });
  const [cashiers, setCashiers] = useState([]);

  const q = useAsyncData(
    () => {
      const params = {};
      if (status !== "all") params.status = status;
      if (user.role !== "cashier" && storeId) params.store_id = storeId;
      return api.get("/discrepancies", { params }).then((r) => r.data.discrepancies);
    },
    [status, storeId, user.role]
  );
  const items = q.data || [];
  const load = q.refresh;

  const openAlloc = async (d) => {
    setAllocTarget(d);
    setAllocNote(d.allocation_note || "");
    try {
      const { data } = await api.get("/store-cashiers", { params: { store_id: d.store_id, business_date: d.business_date } });
      setCashiers(data.cashiers);
      const rows = data.cashiers.map((c) => {
        const ex = (d.allocations || []).find((a) => a.cashier_id === c.id);
        return { cashier_id: c.id, name: c.name, amount: ex ? fromPaise(ex.amount_paise) : "" };
      });
      (d.allocations || []).forEach((a) => {
        if (!rows.find((r) => r.cashier_id === a.cashier_id))
          rows.push({ cashier_id: a.cashier_id, name: a.cashier_name, amount: fromPaise(a.amount_paise) });
      });
      setAllocRows(rows);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const equalSplit = () => {
    const ids = allocRows.map((r) => r.cashier_id);
    if (!ids.length) return;
    api.patch(`/discrepancies/${allocTarget.id}/allocate`, { allocations: [], equal_split: ids, note: allocNote || "Equal split" })
      .then(() => { toast.success("Split equally"); setAllocTarget(null); load(); })
      .catch((e) => toast.error(errMsg(e)));
  };

  const saveAlloc = () => {
    const allocations = allocRows.filter((r) => toPaise(r.amount) > 0)
      .map((r) => ({ cashier_id: r.cashier_id, amount_paise: toPaise(r.amount) }));
    api.patch(`/discrepancies/${allocTarget.id}/allocate`, { allocations, note: allocNote })
      .then(() => { toast.success("Responsibility allocated"); setAllocTarget(null); load(); })
      .catch((e) => toast.error(errMsg(e)));
  };

  const doSettle = () => {
    if (!toPaise(settle.amount)) { toast.error("Amount required"); return; }
    api.post(`/discrepancies/${settleTarget.id}/settle`, {
      amount_paise: toPaise(settle.amount), mode: settle.mode,
      bank_id: settle.mode === "bank" ? settle.bank_id : null,
      note: settle.note || null, related_bill_no: settle.related_bill_no || null,
    }).then(() => {
      toast.success("Settlement recorded on today's date, linked to the original discrepancy");
      setSettleTarget(null); setSettle({ amount: "", mode: "cash", bank_id: "", note: "", related_bill_no: "" });
      load();
    }).catch((e) => toast.error(errMsg(e)));
  };

  const canManage = ["admin", "manager", "accountant"].includes(user.role);
  const allocAssigned = allocRows.reduce((sum, r) => sum + toPaise(r.amount), 0);
  const allocLeft = (allocTarget?.amount_paise ?? 0) - allocAssigned;
  const settleOutstanding = settleTarget
    ? (settleTarget.outstanding_paise ?? (settleTarget.amount_paise - (settleTarget.settled_paise || 0)))
    : 0;
  const settleOver = toPaise(settle.amount) > settleOutstanding;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-xl font-semibold arch-underline">Discrepancy ledger</h1>
          <p className="text-sm text-muted-foreground">Shortages &amp; excesses with responsibility allocation and later settlements</p>
          <p className="text-xs text-muted-foreground" data-testid="discrepancy-scope-note">
            Showing the store picked on the right — a shortage can stay unallocated while it is investigated.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StoreDatePicker hideDate />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-[190px]" data-testid="discrepancy-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="partially_adjusted">Partially Adjusted</SelectItem>
              <SelectItem value="adjusted">Adjusted</SelectItem>
              <SelectItem value="closed_unexplained">Closed Unexplained</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="table-compact">
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Type</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Settled</TableHead>
              <TableHead>Responsibility</TableHead><TableHead>Age</TableHead>
              <TableHead>Status</TableHead><TableHead className="w-[150px]"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {q.error && <TableRow><TableCell colSpan={8}><LoadErrorState error={q.error} onRetry={q.reload} title="Could not load discrepancies" /></TableCell></TableRow>}
              {q.loading && <TableRow><TableCell colSpan={8} className="text-center py-8 text-sm text-muted-foreground animate-pulse">Loading…</TableCell></TableRow>}
              {!q.loading && !q.error && items.length === 0 && <TableRow><TableCell colSpan={8}><EmptyState icon={AlertTriangle} title="No discrepancies" /></TableCell></TableRow>}
              {items.map((d) => (
                <TableRow key={d.id} data-testid={`discrepancy-row-${d.id.slice(0, 6)}`}>
                  <TableCell className="font-mono-num text-xs">{d.business_date}</TableCell>
                  <TableCell>
                    <StatusBadge status={d.type === "shortage" ? "pending" : "matched"} label={d.type === "shortage" ? "Shortage" : "Excess"} />
                  </TableCell>
                  <TableCell className="amount-cell"><Money paise={d.type === "shortage" ? -d.amount_paise : d.amount_paise} colored /></TableCell>
                  <TableCell className="amount-cell"><Money paise={d.settled_paise || 0} /></TableCell>
                  <TableCell className="text-xs">
                    {(d.allocations || []).map((a) => (
                      <span key={a.cashier_id} className="inline-block mr-2">{a.cashier_name}: <Money paise={a.amount_paise} className="text-muted-foreground" /></span>
                    ))}
                    {d.unallocated_paise > 0 && (
                      <span className="inline-block mr-2 text-[hsl(var(--warning))] font-semibold" data-testid={`discrepancy-unallocated-${d.id.slice(0, 6)}`}>
                        {(d.allocations || []).length ? <>Unallocated <Money paise={d.unallocated_paise} /></> : "Responsibility not allocated"}
                      </span>
                    )}
                    {d.allocation_review_needed && (
                      <span className="block text-[10px] text-[hsl(var(--danger))] font-semibold" data-testid={`discrepancy-review-${d.id.slice(0, 6)}`}>
                        Amount changed after allocation — review the split
                      </span>
                    )}
                    {d.note && <span className="block text-[10px] text-muted-foreground truncate max-w-[220px]">{d.note}</span>}
                    {(d.settlements || []).length > 0 && (
                      <span className="block text-[10px] text-[hsl(var(--info))]">{d.settlements.length} settlement(s), last on {d.settlements[d.settlements.length - 1].date}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{d.age_days != null ? `${d.age_days}d` : "—"}</TableCell>
                  <TableCell><StatusBadge status={d.status} /></TableCell>
                  <TableCell>
                    {canManage && d.status !== "adjusted" && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openAlloc(d)} data-testid={`discrepancy-allocate-${d.id.slice(0, 6)}`}>Allocate</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setSettleTarget(d); setSettle((s) => ({ ...s, amount: fromPaise(d.amount_paise - (d.settled_paise || 0)) })); }} data-testid={`discrepancy-settle-${d.id.slice(0, 6)}`}>Settle</Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Allocation dialog */}
      <Dialog open={!!allocTarget} onOpenChange={(o) => !o && setAllocTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Allocate responsibility — <Money paise={allocTarget?.amount_paise} /></DialogTitle>
            <DialogDescription className="text-xs">
              A responsibility note and audit record — not a payroll deduction, and it changes neither the cash count nor the next day's opening.
              Assign all of it, split it between cashiers, or leave part unallocated while it is investigated.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {allocRows.map((r, i) => (
              <div key={r.cashier_id} className="flex items-center gap-2">
                <span className="text-sm flex-1">{r.name}</span>
                <MoneyInput value={r.amount} onChange={(v) => setAllocRows(allocRows.map((x, j) => j === i ? { ...x, amount: v } : x))}
                  className="h-9 w-[140px]" testId={`alloc-amount-${i}`} />
              </div>
            ))}
            <div className="flex justify-between text-xs border-t pt-2" data-testid="alloc-tally">
              <span className="text-muted-foreground">Assigned <Money paise={allocAssigned} className="text-foreground" /> of <Money paise={allocTarget?.amount_paise ?? 0} className="text-foreground" /></span>
              <span className={allocLeft < 0 ? "text-[hsl(var(--danger))] font-semibold" : allocLeft > 0 ? "text-[hsl(var(--warning))] font-semibold" : "text-[hsl(var(--success))] font-semibold"}>
                {allocLeft < 0 ? <>Over by <Money paise={-allocLeft} /></> : allocLeft > 0 ? <>Unallocated <Money paise={allocLeft} /></> : "Fully allocated"}
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={equalSplit} className="w-full" data-testid="equal-split-button">
              <SplitSquareHorizontal className="h-3.5 w-3.5 mr-1.5" />Equal split among all listed cashiers
            </Button>
            <Textarea value={allocNote} onChange={(e) => setAllocNote(e.target.value)} placeholder="Allocation note" data-testid="alloc-note-input" />
          </div>
          <DialogFooter><Button onClick={saveAlloc} disabled={allocLeft < 0} data-testid="alloc-save-button">Save allocation</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settle dialog */}
      <Dialog open={!!settleTarget} onOpenChange={(o) => !o && setSettleTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record later settlement</DialogTitle>
            <DialogDescription className="text-xs">
              Money moves <b>today</b> — the entry posts on today's date and links back to the discrepancy of {settleTarget?.business_date}. The old Rokad is annotated, not altered.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Amount (₹)</Label>
              <MoneyInput value={settle.amount} onChange={(v) => setSettle({ ...settle, amount: v })} testId="settle-amount-input" />
              <p className={`text-[11px] ${settleOver ? "text-[hsl(var(--danger))] font-semibold" : "text-muted-foreground"}`} data-testid="settle-outstanding-hint">
                {settleOver
                  ? <>More than the <Money paise={settleOutstanding} /> still outstanding</>
                  : <>Still outstanding on this {settleTarget?.type}: <Money paise={settleOutstanding} className="text-foreground" /></>}
              </p>
            </div>
            <div className="flex gap-2">
              <Select value={settle.mode} onValueChange={(v) => setSettle({ ...settle, mode: v })}>
                <SelectTrigger className="w-[130px]" data-testid="settle-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank">Bank</SelectItem>
                </SelectContent>
              </Select>
              {settle.mode === "bank" && (
                <Select value={settle.bank_id} onValueChange={(v) => setSettle({ ...settle, bank_id: v })}>
                  <SelectTrigger className="flex-1" data-testid="settle-bank-select"><SelectValue placeholder="Bank" /></SelectTrigger>
                  <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-1.5"><Label>Original bill no (optional)</Label>
              <Input value={settle.related_bill_no} onChange={(e) => setSettle({ ...settle, related_bill_no: e.target.value })} className="font-mono-num" data-testid="settle-bill-input" /></div>
            <Textarea value={settle.note} onChange={(e) => setSettle({ ...settle, note: e.target.value })} placeholder="Note" data-testid="settle-note-input" />
          </div>
          <DialogFooter><Button onClick={doSettle} disabled={settleOver} data-testid="settle-save-button">Record settlement</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
